import { ChangeEvent, KeyboardEventHandler, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import './SecuritySearchModal.css'
import { Box, Dialog, DialogContent, Divider, InputAdornment, Stack, styled, TextField, Typography } from '@mui/material'
import { Security, SecurityCategory, SecurityCategory_CategoryCode, SecurityExpression } from '../../proto/generated/security'
import { exchangeToJSON } from '../../proto/generated/common'
import SecurityCategoryItem from '../securityCategoryItem/SecurityCategoryItem'
import { getCategories, lookupSymbols } from '../../services/apis/securities'
import { selectUserSecurityExpressions } from '../../selectors/security'
import {
    cleanUpBadParenthesis,
    convertDisplayExpressionToExpression,
    convertExpressionToDisplayExpression,
    isSecurityExpressionTerm,
    getHighlightRange,
    getOperatorIndices,
    getOperatorRegex,
    operandEndingRegex,
    replaceNamesWithExpressions
} from '../../util/expression'
import { debounce } from 'throttle-debounce'
import { ExpressionCallbackData, ExpressionType } from './typings'
import OperatorsPanel from '../operatorsPanel/OperatorsPanel'
import { getExchangeAndSymbolFromKey, getSecurityAsync, getSecurityKey } from '../../util/security'
import commonActions from '../../actions/common.actions'
import { selectCurrentTheme, selectMultiPaneFullScreen, selectRecentExpressions } from '../../selectors/common'
import { deleteSecurityExpression } from '../../services/apis/expression'
import SecurityInfiniteScroll from './SecurityInfiniteScroll'
import { deleteUserExpressions } from '../../reducer/security.reducer'
import { selectActiveLayoutId, selectPrimarySecurity, selectSecuritySearchOpen } from '../../selectors/home'
import { EXP_FILTER_DELIMTER, MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import SearchIcon from '../../assets/icons/SearchIcon'
import { v4 as uuid } from 'uuid'
import useSecurityExpressionToName from '../../hooks/useExpressionNames'
import ClearIcon from '../../assets/icons/ClearIcon'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { ExtraSmallButton } from '../custom'
import { WidgetTypes } from '../widgetDashboard/widgetDashboard.enums'
import { selectLayoutWidgets } from '../../selectors/widgetDashboard'
import { selectActiveChartId } from '../../selectors/chart'
import ROUTES from '../../assets/routes'
import { matchPath } from 'react-router-dom'

interface Props {
    open: boolean
    enableExpressions: boolean
    onCloseClicked: () => void
    deleteIcon?: boolean
    onSecuritySelected: Function
    selectedItems?: Set<string>
    tailIcon?: boolean
    watchlistMode?: boolean
    forceCategoryCode?: SecurityCategory_CategoryCode;
    showOnlySimulatorSecurities?: boolean
    showOnlyFOSecurities?: boolean
    compareSecurityPicker?: boolean
}

const SearchDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        minHeight: '70vh',
        maxHeight: '70vh',
    },
    zIndex: 13000
}))

const SecuritySearchModal = (props: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const darkTheme = useSelector(selectCurrentTheme)
    const {open, onCloseClicked, onSecuritySelected, deleteIcon, tailIcon, selectedItems, enableExpressions, watchlistMode, forceCategoryCode, showOnlySimulatorSecurities = false, showOnlyFOSecurities, compareSecurityPicker} = props
    const securitySearchOpen = useSelector(selectSecuritySearchOpen)
    const { key, expressionMode: expressionEnabled, primary } = useSelector(selectPrimarySecurity)
    const security = expressionEnabled ? null : primary
    const { expressionSecurities } = useSelector(selectPrimarySecurity)
    const globalExpressionToName = useSecurityExpressionToName()
    const [inputValue, setInputValue] = useState<string>('')
    const [expressionMode, setExpressionMode] = useState(enableExpressions && expressionEnabled)
    const symbolsUsed = useRef<Record<string, Security>>(
        expressionSecurities.reduce(
            (acc: Record<string, Security>, sec: Security) => {
                acc[`${exchangeToJSON(sec.exchange)}:${sec.symbol}`] = sec
                return acc
            },
            {} as Record<string, Security>
        )
    )
    const operatorIndices = useMemo(() => getOperatorIndices(inputValue), [inputValue])
    const [searchTerm, setSearchTerm] = useState<string>('')
    const cachedSecurities = useRef<Security[]>([])
    const cachedStrategyExpressions = useRef<SecurityExpression[]>([])
    const cursor = useRef<number | null>(inputValue.length)
    const [mounted, setMounted] = useState(false)
    const [securityCategories, setSecurityCategories] = useState<SecurityCategory[]>()
    const [selectedCategoryCode, setSelectedCategoryCode] = useState<SecurityCategory_CategoryCode>(SecurityCategory_CategoryCode.ALL)
    const nameToDisplayExpression = useRef<Map<string, string>>(new Map())
    const displayExpressionToName = useRef<Map<string, string>>(new Map())
    const savedExpressions: SecurityExpression[] | undefined = useSelector(selectUserSecurityExpressions)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const viewKey = useSelector(selectActiveChartId)
    const currentWidget = useMemo(() => widgets[Number(viewKey?.split('-')[1]) ?? 0], [viewKey, widgets])
    const recentExpressions = useSelector(selectRecentExpressions)

    const allExpressions = useMemo(() => {
        if (!enableExpressions) {
            return []
        }
        return (savedExpressions?.filter((e) => e.securities?.length > 0 && !e.expired) ?? [])
            .concat(recentExpressions)
            .reduce((unique: SecurityExpression[], e) => {
                if (!unique.some((item) => item.expression === e.expression)) {
                    unique.push(e)
                }
                return unique
            }, [])
            .map((e) => {
                const displayExpression = convertExpressionToDisplayExpression(e.expression, e.securities)
                if (e.name) {
                    nameToDisplayExpression.current.set(e.name, displayExpression)
                    displayExpressionToName.current.set(displayExpression, e.name)
                }
                return {
                    id: e.id ?? e.expression,
                    name: e.name ?? convertExpressionToDisplayExpression(e.expression, e.securities),
                    expression: e.expression,
                    securities: e.securities,
                    expired: false
                }
            })
    }, [savedExpressions, recentExpressions])
    const [focusedIndex, setFocusedIndex] = useState<number>(NaN)
    const maxItemsFocusable = useRef<number>(0)
    const comparePickerSubmitRef = useRef<(() => void) | null>(null)

    const dispatch = useDispatch()
    useEffect(() => {
        ;(async () => {
            const securityCategories = await getCategories()
            let categories = securityCategories.categories
            // watchlistMode should always show all securities regardless of current route
            if (
                !watchlistMode && (
                    showOnlyFOSecurities ||
                    currentWidget?.widgetType === WidgetTypes.MULTI_STRIKE_CHART ||
                    currentWidget?.widgetType === WidgetTypes.OI_CHART ||
                    currentWidget?.widgetType === WidgetTypes.OPEN_INTEREST_CHART ||
                    currentWidget?.widgetType === WidgetTypes.OPTION_CHAIN ||
                    matchPath(ROUTES.strategyBuilder, window.location.pathname) ||
                    matchPath(ROUTES.optionChain, window.location.pathname) ||
                    matchPath(ROUTES.openInterest, window.location.pathname) ||
                    matchPath(ROUTES.openInterestChange, window.location.pathname) ||
                    matchPath(ROUTES.multiStrikeChart, window.location.pathname) ||
                    matchPath(ROUTES.scalper, window.location.pathname)
                )
            ) {
                categories = categories.filter(
                    (category: any) =>
                        category.code !== SecurityCategory_CategoryCode.FUTURES &&
                        category.code !== SecurityCategory_CategoryCode.OPTIONS &&
                        category.code !== SecurityCategory_CategoryCode.STOCKS &&
                        category.code !== SecurityCategory_CategoryCode.INDEX &&
                        category.code !== SecurityCategory_CategoryCode.ALL
                )
            }
            setSecurityCategories(categories)
        })()
    }, [])

    function onCategorySelected(code: SecurityCategory_CategoryCode) {
        if (forceCategoryCode) return;
        setSelectedCategoryCode(code)

    }
    useEffect(() => {
        if (inputValue.length === 0) {
            let inputVal = ''
            if (securitySearchOpen.key) {
                if (securitySearchOpen.isExpression) {
                    const backendKey = key || securitySearchOpen.key
                    inputVal =
                        globalExpressionToName.get(backendKey) ??
                        convertExpressionToDisplayExpression(backendKey, expressionSecurities)
                    setInputValue(inputVal)
                } else {
                    getSecurityAsync(securitySearchOpen.key).then((security) => {
                        if (security && security.symbol) {
                            const val = getSecurityKey(security.exchange, security.symbol!)
                            setInputValue(val)
                            const [, symbol] = getExchangeAndSymbolFromKey(val)
                            setSearchTerm(symbol)
                        }
                    })
                    return
                }
            } else {
                inputVal =
                    enableExpressions && expressionEnabled
                        ? globalExpressionToName.get(key) ??
                          convertExpressionToDisplayExpression(key, expressionSecurities)
                        : security
                          ? `${exchangeToJSON(security.exchange)}:${security.symbol}`
                          : ''
                setInputValue(inputVal)
            }
            if (inputVal) {
                if (getOperatorRegex().test(inputVal)) {
                    const [start, end] = getHighlightRange(inputVal, 0, 0)
                    const leg = inputVal.substring(start, end).trim()
                    const [, scrip] = leg.split(':')
                    setSearchTerm(scrip || leg.split(':')[0] || '')
                } else {
                    const [, symbol] = getExchangeAndSymbolFromKey(inputVal)
                    setSearchTerm(symbol)
                }
            }
        }
    }, [
        enableExpressions,
        expressionEnabled,
        security,
        key,
        expressionSecurities,
        globalExpressionToName,
        securitySearchOpen.key,
        securitySearchOpen.isExpression
    ])

    useEffect(() => {
        if (checkValueForExpression(inputValue)) {
            setExpressionMode(true)
        } else {
            setExpressionMode(false)
        }
        if (!inputRef.current || !cursor.current) return
        inputRef.current.setSelectionRange(cursor.current, cursor.current)
    }, [inputValue])

    const onInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const target = event.target
            const value = target.value.toUpperCase()
            if (!expressionMode) {
                const [, symbol] = getExchangeAndSymbolFromKey(value)
                setSearchTerm(symbol)
            }
            setFocusedIndex(NaN)
            setInputValue(value)
        },
        [expressionMode]
    )

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (mounted && inputValue.length > 1) {
            inputRef.current?.select()
        }
    }, [mounted])

    const checkValueForExpression = useCallback(
        (value: string) => {
            return nameToDisplayExpression.current.has(value) || getOperatorRegex().test(value)
        },
        [nameToDisplayExpression]
    )

    const onExpressionSelected = useCallback(
        (expression: SecurityExpression, selected: boolean, securities: Security[], type: ExpressionType) => {
            const securityExpression = allExpressions.find((exp) => exp.expression === expression?.expression)
            if (!securities && !securityExpression) {
                commonActions.setApiError(dispatch, { error: true, message: 'Something went wrong', errorType: 'error' })
                return
            }
            onSecuritySelected(
                {
                    expression: cleanUpBadParenthesis(expression.expression, false),
                    securities: securities ?? securityExpression?.securities,
                    expressionMode: true,
                    expressionId: expression.id ?? cleanUpBadParenthesis(expression.expression, false),
                    type: type,
                    name: expression.name
                },
                selected
            )
        },
        [onSecuritySelected, allExpressions, dispatch]
    )

    const onExpressionDeleted = useCallback(
        (expression: string) => {
            const securityExpression = (savedExpressions ?? []).find((exp) => exp.expression === expression)
            commonActions.deleteRecentExpression(dispatch, expression)
            if (securityExpression) {
                deleteSecurityExpression({ expression: securityExpression }).then(() => {
                    dispatch(deleteUserExpressions(securityExpression))
                })
            }
        },
        [savedExpressions, dispatch]
    )

    const onSecuritySelect = useCallback(
        (data: Security | SecurityExpression, selected: boolean, focused: boolean, type: ExpressionType | 'SECURITY') => {
            const isExpression = ['EXPRESSION', 'STRATEGY_EXPRESSION'].includes(type)
            if (!expressionMode || (expressionMode && isExpression)) {
                if (isExpression) {
                    onExpressionSelected(
                        focused
                            ? {
                                  ...(data as SecurityExpression),
                                  expression: cleanUpBadParenthesis((data as SecurityExpression).expression)
                              }
                            : (data as SecurityExpression),
                        selected,
                        (data as SecurityExpression).securities,
                        type as ExpressionType
                    )
                } else {
                    onSecuritySelected({ expression: '', securities: [data], expressionMode: false }, selected)
                }
                return
            }
            let start = isExpression ? 0 : inputRef.current!.selectionStart || 0
            let end = isExpression ? inputValue.length : inputRef.current!.selectionEnd || 0
            if (end === start) {
                let [highlightStart, highlightEnd] = getHighlightRange(inputValue, start!, end!)
                start = highlightStart
                end = highlightEnd
            }

            let exp = inputValue.split('')
            let term
            if (isExpression) {
                const displayExpression = convertExpressionToDisplayExpression((data as SecurityExpression).expression, (data as SecurityExpression).securities)
                term = displayExpressionToName.current.get(displayExpression) ?? displayExpression
                ;(data as SecurityExpression).securities?.forEach((sec) => {
                    symbolsUsed.current[(exchangeToJSON(sec.exchange) || 'NSE') + ':' + sec.symbol] = sec
                })
            } else {
                term = (exchangeToJSON((data as Security).exchange) || 'NSE') + ':' + (data as Security).symbol
                symbolsUsed.current[term] = data as Security
            }
            exp.splice(start, end - start, term)
            cursor.current = start + term.length
            setInputValue(exp.join(''))
            inputRef.current!.focus()
            if (!isExpression && operatorIndices.length === 0) setSearchTerm((data as Security)?.symbol || '')
        },
        [expressionMode, inputValue, operatorIndices.length, onExpressionSelected, onSecuritySelected]
    )

    const onSelectionChange = debounce(100, (event: any) => {
        if (!Number.isNaN(focusedIndex)) {
            cursor.current = null
            return
        }
        const selectionStart = event.target.selectionStart
        const selectionEnd = event.target.selectionEnd

        if (!inputRef.current) return

        const [start, end] = getHighlightRange(inputValue, selectionStart, selectionEnd)
        const leftIndex = start,
            rightIndex = end
        if (event.nativeEvent instanceof MouseEvent) {
            if (selectionStart !== selectionEnd || selectionEnd !== inputValue.length) {
                inputRef.current.setSelectionRange(leftIndex, rightIndex)
            }
        }
        cursor.current = null
        let search = inputValue.substring(leftIndex, rightIndex)

        if (checkValueForExpression(search)) {
            setSearchTerm('')
        } else {
            const [exchange, scrip] = search.split(':')
            search = scrip || exchange
            // if (selectionEnd - selectionStart > 0 && end - start > 0) {
            //     setSearchTerm('')
            // } else {
            setSearchTerm(search)
            // }
        }
    })

    const onOperatorClick = useCallback(
        (value: string) => {
            const selectionStart = inputRef.current!.selectionStart
            const selectionEnd = inputRef.current!.selectionEnd
            let [start, end] = getHighlightRange(inputValue, selectionStart!, selectionEnd!)
            let operand = inputValue.substring(start, end)
            if (getOperatorRegex().test(operand)) {
                operand = cleanUpBadParenthesis(operand, true)
            }

            if (value === '1/') {
                operand = `1/${operand}`
                cursor.current = start + operand.length
            } else {
                operand += value
                cursor.current = start + operand.length + 1
            }

            let exp = inputValue.split('')
            exp.splice(start, end - start, operand)
            setInputValue(exp.join(''))
            inputRef.current!.focus()
            setSearchTerm('')
        },
        [inputValue]
    )

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            if (e.key === 'ArrowDown') {
                if (Number.isNaN(focusedIndex)) {
                    setFocusedIndex(0)
                } else if (focusedIndex + 1 <= maxItemsFocusable.current - 1) {
                    setFocusedIndex(focusedIndex + 1)
                }
                e.preventDefault()
            } else if (e.key === 'ArrowUp') {
                if (Number.isNaN(focusedIndex)) {
                    setFocusedIndex(0)
                } else if (focusedIndex === 0) {
                    setFocusedIndex(NaN)
                } else {
                    setFocusedIndex(focusedIndex - 1)
                }
                e.preventDefault()
            }
        },
        [focusedIndex]
    )

    const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            if (e.key === 'Enter') {
                if (compareSecurityPicker && !expressionMode && !Number.isNaN(focusedIndex)) {
                    comparePickerSubmitRef.current?.()
                    e.preventDefault()
                    return
                }
                const rawInputValue = replaceNamesWithExpressions(inputValue, nameToDisplayExpression.current)
                if (operandEndingRegex.test(rawInputValue)) {
                    return
                }
                const indices = getOperatorIndices(rawInputValue)
                const securitiesToFetch = new Set<string>()
                const invalidSymbols = new Set<string>()
                const securities: Security[] = []
                indices.forEach(([start, end]) => {
                    if (end > start) {
                        const term = rawInputValue.substring(start, end + 1).trim()
                        if (isSecurityExpressionTerm(term)) {
                            let [code] = term.trim().split(EXP_FILTER_DELIMTER)
                            code = code.trim()
                            const sec = symbolsUsed.current[code]

                            if (!sec || sec.categoryCode === undefined) {
                                if (term.indexOf(':') === -1) {
                                    invalidSymbols.add(code)
                                } else {
                                    securitiesToFetch.add(code)
                                }
                            } else {
                                securities.push(sec)
                            }
                        }
                    }
                })
                if (invalidSymbols.size > 0) {
                    return
                }
                if (securitiesToFetch.size > 0) {
                    try {
                        const response = await lookupSymbols(
                            Array.from(securitiesToFetch).map((sec) => {
                                const [exchange, symbol] = getExchangeAndSymbolFromKey(sec)
                                return {
                                    exchange,
                                    symbol
                                }
                            })
                        )
                        if (response && response.securities.length) {
                            response.securities.forEach((sec) => {
                                symbolsUsed.current[`${exchangeToJSON(sec.exchange)}:${sec.symbol}`] = sec
                                securities.push(sec)
                            })
                        }
                    } catch (e) {
                        console.error(e)
                    }
                }
                let finalExpression = convertDisplayExpressionToExpression(rawInputValue, securities)

                if (selectedItems && selectedItems?.has(finalExpression)) {
                    commonActions.setApiError(dispatch, {
                        error: true,
                        message: 'Expression already added',
                        errorType: 'error'
                    })
                    return
                }
                const data: ExpressionCallbackData = {
                    expression: cleanUpBadParenthesis(finalExpression, false),
                    expressionId: uuid(),
                    securities: securities,
                    expressionMode: true,
                    type: 'EXPRESSION'
                }
                if (securities.length === 1 && finalExpression === getSecurityKey(securities[0].exchange, securities[0].code)) {
                    data.expression = ''
                    data.expressionMode = false
                    data.type = undefined

                    setExpressionMode(false)
                } else if (displayExpressionToName.current.has(rawInputValue)) {
                    data.name = displayExpressionToName.current.get(rawInputValue)!
                    if (!new Set(allExpressions.map((exp) => exp.name)).has(data.name)) {
                        data.type = 'STRATEGY_EXPRESSION'
                    }
                }
                onSecuritySelected(data, true)
            }
        },
        [inputValue, selectedItems, onSecuritySelected, dispatch, allExpressions, compareSecurityPicker, expressionMode, focusedIndex]
    )

    const onMaxFocusableChange = useCallback((max: number) => {
        maxItemsFocusable.current = max
    }, [])

    const reduxContainer = useSelector(selectMultiPaneFullScreen)
    // Use native fullscreen element (covers option-chain fullscreen) OR fall back to Redux multipane fullscreen
    const nativeFullscreenEl = useSyncExternalStore(
        (cb) => { document.addEventListener('fullscreenchange', cb); return () => document.removeEventListener('fullscreenchange', cb) },
        () => document.fullscreenElement as HTMLElement | null,
        () => null
    )
    const container = nativeFullscreenEl ?? (reduxContainer ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null)

    return (
        <SearchDialog
            open={open}
            onClose={onCloseClicked}
            aria-labelledby="customized-dialog-title"
            maxWidth={'md'}
            container={container ?? undefined}
            fullWidth
        >
            <DraggableDialogTitle id="customized-dialog-title">
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                    <Typography variant={'h6'}>Symbol Search</Typography>
                    <ExtraSmallButton onClick={onCloseClicked} aria-label="close" color={'inherit'}>
                        <ClearIcon fontSize={'small'} />
                    </ExtraSmallButton>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        position: 'relative',
                        marginBottom: 1
                    }}
                >
                    <TextField
                        variant="standard"
                        fullWidth
                        inputRef={inputRef}
                        multiline={false}
                        placeholder="Search here..."
                        value={inputValue}
                        disabled={showOnlySimulatorSecurities}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 24, opacity: 0.5 }} />
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                        onChange={onInputChange}
                        onSelect={onSelectionChange}
                        onKeyUp={onKeyUp}
                        onKeyDown={onKeyDown}
                        autoFocus
                        autoComplete="off"
                    />
                    {(enableExpressions && !showOnlySimulatorSecurities) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '24px', flexGrow: 1 }}>
                            <Divider orientation="vertical" sx={{ marginRight: (theme) => theme.spacing(1) }} />
                            <OperatorsPanel onClick={(value) => onOperatorClick(value)} enabled={true} />
                        </Box>
                    )}
                </Box>
                {!forceCategoryCode && (<Stack direction={'row'} justifyContent={'flex-start'}>
                    {(securityCategories ?? [])
                        .filter((category) => {
                            // watchlistMode always shows all categories regardless of current route
                            if (
                                !watchlistMode && (
                                    showOnlyFOSecurities ||
                                    currentWidget?.widgetType === WidgetTypes.MULTI_STRIKE_CHART ||
                                    currentWidget?.widgetType === WidgetTypes.OI_CHART ||
                                    currentWidget?.widgetType === WidgetTypes.OPEN_INTEREST_CHART ||
                                    currentWidget?.widgetType === WidgetTypes.OPTION_CHAIN ||
                                    matchPath(ROUTES.optionChain, window.location.pathname) ||
                                    matchPath(ROUTES.strategyBuilder, window.location.pathname) ||
                                    matchPath(ROUTES.openInterest, window.location.pathname) ||
                                    matchPath(ROUTES.openInterestChange, window.location.pathname) ||
                                    matchPath(ROUTES.multiStrikeChart, window.location.pathname) ||
                                    matchPath(ROUTES.scalper, window.location.pathname)
                                )
                            ) {
                                return category.code !== SecurityCategory_CategoryCode.SPREADS
                            }
                            return true
                        })
                        .map((category) => (
                            <SecurityCategoryItem
                                key={category.code}
                                onSelected={onCategorySelected}
                                selected={selectedCategoryCode === category.code}
                                category={category}
                                darkTheme={darkTheme}
                                inputRef={inputRef}
                            />
                        ))}
                </Stack>)}
            </DraggableDialogTitle>
            <DialogContent sx={{ padding: 0 }}>
                <SecurityInfiniteScroll
                    categoryCode={forceCategoryCode ?? selectedCategoryCode}
                    searchText={searchTerm || ''}
                    tailIcon={tailIcon}
                    deleteIcon={deleteIcon}
                    expressions={allExpressions}
                    cachedSecurities={cachedSecurities}
                    cachedStrategyExpressions={cachedStrategyExpressions}
                    highlightSelected={watchlistMode || false}
                    expressionToName={displayExpressionToName}
                    nameToExpression={nameToDisplayExpression}
                    onSecuritySelect={onSecuritySelect}
                    onExpressionDeleted={onExpressionDeleted}
                    selectedItems={selectedItems}
                    setInputValue={setInputValue}
                    focusedIndex={focusedIndex}
                    onMaxFocusableChange={onMaxFocusableChange}
                    expressionMode={expressionMode}
                    key={selectedCategoryCode + searchTerm}
                    showOnlySimulatorSecurities={showOnlySimulatorSecurities}
                    showOnlyFOSecurities={showOnlyFOSecurities}
                    watchlistMode={watchlistMode}
                    compareSecurityPicker={compareSecurityPicker}
                    comparePickerSubmitRef={comparePickerSubmitRef}
                    onCompareSecurityPick={
                        compareSecurityPicker
                            ? (security, placement) => {
                                  onSecuritySelected(
                                      {
                                          expression: '',
                                          securities: [security],
                                          expressionMode: false,
                                          comparePlacement: placement
                                      },
                                      true
                                  )
                              }
                            : undefined
                    }
                />
            </DialogContent>
        </SearchDialog>
    )
}

export default SecuritySearchModal
